<template>
  <div class="sign-up pop-up">
    <div class="sign-up__inner">
      <div class="sign-up__content">
        <div class="sign-up__logo-text-box">
          <button
            v-if="isTemporaryUser()"
            class="login__back-15807"
            @click="router.back()"
          >
            <span class="iconfont iback"></span> Back
          </button>
          <a class="sign-up__logo" :href="isNativeIOSApp ? '#' : '/'">
            <img
              class="sign-up__logo-img"
              src="@/assets.app/images/logo-sign-up.svg"
              alt="Shortform"
            />
          </a>
        </div>
      </div>
      <div class="sign-up__form-box">
        <div class="shortform-sign-up">
          <h2 class="shortform-sign-up__title">Log In</h2>
          <form
            class="shortform-sign-up__form"
            role="form"
            novalidate
            @submit.prevent="submitForm"
          >
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="login_email"
                >Email Address</label
              >
              <InputWithContextMenu
                id="login_email"
                v-model="view.model.email"
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('email')"
                type="email"
                placeholder=""
                :autofocus="!isNativeIOSApp"
              />
            </div>
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="login_password"
                >Password</label
              >
              <InputWithContextMenu
                id="login_password"
                ref="passwordInput"
                v-model="view.model.password"
                type="password"
                autocomplete="current-password"
                placeholder=""
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('password')"
              />
              <FormError
                :errors="view.validation.errors"
                field="email"
                style="font-size: 90%; display: block"
              />
              <FormError
                :errors="view.validation.errors"
                field="password"
                style="font-size: 90%; display: block"
              />
            </div>
            <div class="shortform-sign-up__group">
              <router-link
                :to="{ name: 'password_forgot', query: route.query }"
                class="shortform-sign-up__forgot-password"
              >
                <strong>Forgot your password?</strong>
              </router-link>
            </div>
            <div class="shortform-sign-up__group">
              <v-wait for="Login" style="display: flex">
                <template v-slot:waiting>
                  <div class="shortform-sign-up__submit button button--theme">
                    Logging In
                    <button-spinner />
                  </div>
                </template>
                <button
                  class="shortform-sign-up__submit button button--theme"
                  type="submit"
                >
                  Log In
                </button>
              </v-wait>
            </div>
          </form>
          <SocialAuth
            :onStart="onStart"
            :onSuccess="onSuccess"
            :onError="onError"
            :authMode="authMode"
          />
          <div class="shortform-sign-up__other-way">
            <p class="shortform-sign-up__log-in">
              New to Shortform?
              <router-link :to="signupRoute" style="color: #ffb600"
                ><strong>Sign up now</strong>.</router-link
              >
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { LoginView } from '@/models/auth/models'
import { platform } from '@/services/platform'
import { authCssFix } from '@/helpers/html'
import { assertNotNull } from '@/helpers/typing'
import { auth } from '@/services/auth'
import { notificationService } from '@/services/notification'
import { backend } from '@/services/backend'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'

import SocialAuth from '@/app/components/auth/SocialAuth.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import { AuthMode } from '@/models/auth/google'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const passwordInput = ref<InstanceType<typeof InputWithContextMenu> | null>(
  null,
)

const view = ref(new LoginView(onStart, onSuccess, onError))

const authMode = ref<AuthMode>(AuthMode.Signin)

const isNativeIOSApp = ref(false)

async function onStart(): Promise<void> {
  wait.start('Login')

  if (isTemporaryUser()) {
    // Log out the user if they are already logged in.
    // This should only be possible during the
    // app_browse_15807 experiment, where users can
    // go to the login page without logging out first.
    await notificationService.unsubscribe()
    await backend.logout()
  }
}

function onSuccess(nextPage: Record<string, unknown> | null): void {
  wait.start('Login')

  const redirectQuery = router.currentRoute.value.query?.redirect

  if (redirectQuery) {
    router.push(redirectQuery as string)
  } else if (nextPage) {
    router.push(nextPage)
  }
  wait.end('Login')
}

function onError(): void {
  wait.end('Login')
}

const _view = computed(() => {
  return assertNotNull(view.value)
})

function isTemporaryUser(): boolean {
  return auth.isTemporaryUser()
}

onMounted(async () => {
  // If user name was passed a prop (from the signup with email form)
  if (route.query.email) {
    _view.value.model.email = route.query.email as string
  }

  isNativeIOSApp.value = await platform.isNativeIOSApp()
  if (isNativeIOSApp.value) {
    _iosPasswordInputFix()
  }

  authCssFix()
})

async function submitForm(): Promise<void> {
  await _view.value.start()
}

const signupRoute = computed(() => {
  const routeData = {
    name: 'signup',
    query: route.query,
  }
  // If we have the email set, pass it on to the signup page.
  if (_view.value.model.email) {
    routeData.query.email = _view.value.model.email
  }
  return routeData
})

/**
 * In the iOS app when autofilling the login credentials using Keychain, 1Password
 * etc., the password field gets filled only visually but the value in our model
 * does not get updated.
 *
 * This is related to a bug in WebKit in that the 'input' event
 * does not get fired for the element when filled by a password manager. So we use
 * the below workaround to explicitly listen to the 'change' event and then update
 * the value of our model.
 *
 * For more information see:
 * - https://github.com/ionic-team/ionic-framework/issues/23335#issuecomment-845270643
 * - https://forum.ionicframework.com/t/vuejs-password-autofill-value-empty/217195/4
 */
function _iosPasswordInputFix(): void {
  passwordInput.value?.$el.addEventListener('change', (ev: Event) => {
    requestAnimationFrame(() => {
      _view.value.model.password = (ev.target as HTMLInputElement).value
    })
  })
}
</script>

<style lang="scss" scoped>
.login__back-15807 {
  display: block;
  margin-bottom: 8px;
  color: $c-black-2;
}
</style>
